import type { FormField, FormFileEntry, FormGroup, FormWidgetKind } from './types';

/** Groups whose title starts with `_` (e.g. `_lookup`) are server-only. */
export const isHiddenGroup = (group: FormGroup): boolean => {
  return typeof group.title === 'string' && group.title.startsWith('_');
};

/**
 * Resolve which widget should render this field. Order:
 *   1. `display_type` (when defined)
 *   2. `uidt`
 *   3. `dt`
 * Comparison is case-insensitive against the canonical kinds.
 */
export const resolveWidgetKind = (field: FormField): FormWidgetKind => {
  const candidates = [field.display_type, field.uidt, field.dt]
    .filter((v): v is string => typeof v === 'string' && v.length > 0)
    .map((v) => v.toLowerCase());

  for (const c of candidates) {
    switch (c) {
      case 'singlelinetext':
      case 'text':
        return 'SingleLineText';
      case 'number':
      case 'integer':
        return 'Number';
      case 'decimal':
      case 'float':
        return 'Decimal';
      case 'date':
        return 'Date';
      case 'datetime':
        return 'datetime';
      case 'week_picker':
        return 'week_picker';
      case 'year_picker':
        return 'year_picker';
      case 'time_interval_picker':
        return 'time_interval_picker';
      case 'checkbox':
        return 'Checkbox';
      case 'singleselect':
      case 'enum':
        return 'SingleSelect';
      case 'multiselect':
        return 'MultiSelect';
      case 'search':
        return 'Search';
      case 'counter':
        return 'counter';
      case 'geo_code':
        return 'geo_code';
      case 'location':
        return 'location';
      case 'versioned_image_picker':
      case 'image_picker':
        return 'versioned_image_picker';
      case 'versioned_pdf_picker':
      case 'pdf_picker':
        return 'versioned_pdf_picker';
    }
  }
  return 'unsupported';
};

/** Returns the human-facing label for a field. Falls back to the title. */
export const labelForField = (field: FormField): string => {
  return field.display_title || field.title;
};

/** Returns the help text for a field (non-blocking, descriptive). */
export const helperForField = (field: FormField): string | undefined => {
  if (field.title_description) return field.title_description;
  if (field.description) return field.description;
  return undefined;
};

/**
 * Build the initial form state for a schema — every field gets a default that
 * matches its widget so the controlled inputs render cleanly without React
 * "uncontrolled → controlled" warnings.
 */
export const buildInitialValues = (groups: FormGroup[]): Record<string, unknown> => {
  const values: Record<string, unknown> = {};
  for (const group of groups) {
    if (isHiddenGroup(group)) continue;
    for (const field of group.items) {
      values[field.title] = defaultValueFor(field);
    }
  }
  return values;
};

const defaultValueFor = (field: FormField): unknown => {
  const kind = resolveWidgetKind(field);
  switch (kind) {
    case 'Checkbox':
      return false;
    case 'MultiSelect':
      return ''; // comma-joined per spec
    case 'counter':
      return field.constraints?.min_value ?? 0;
    case 'geo_code':
      return null;
    case 'versioned_image_picker':
    case 'versioned_pdf_picker':
      return [];
    case 'Search':
    case 'Number':
    case 'Decimal':
    case 'year_picker':
      return '';
    default:
      return '';
  }
};

/** Strip an empty / null value to `undefined` so the API receives clean omissions. */
export const isEmptyValue = (v: unknown): boolean => {
  if (v === null || v === undefined) return true;
  if (typeof v === 'string') return v.trim() === '';
  if (Array.isArray(v)) return v.length === 0;
  return false;
};

const isPositiveInt = (n: unknown): n is number =>
  typeof n === 'number' && Number.isFinite(n) && Number.isInteger(n) && n > 0;

const pickStringMaybe = (v: unknown): string | undefined =>
  typeof v === 'string' && v.length > 0 ? v : undefined;

/**
 * Coerce a versioned-document field value into an array of `FormFileEntry`.
 *
 * Accepted shapes (per `Document-Upload.md` + `PDF-Resource-Type.md`):
 *   - single object `{document_submission_id, document_version_id, filename?, mime_type?}`
 *   - array of those objects (multi-file fields)
 *   - JSON-encoded string of either of the above
 *   - alternate key names: `submission_id` / `version_id` (matches the
 *     `PDF-Resource-Type.md` §2 response shape), and `mime_type` /
 *     `mimetype` interchangeably.
 *
 * Anything missing both IDs is dropped. Entries with `error` are kept so the
 * picker can render failure UI for them.
 */
export const parseFileEntries = (value: unknown): FormFileEntry[] => {
  if (value === null || value === undefined || value === '') return [];

  let raw: unknown = value;
  if (typeof raw === 'string') {
    try {
      raw = JSON.parse(raw);
    } catch {
      return [];
    }
  }

  const items = Array.isArray(raw) ? raw : [raw];
  const entries: FormFileEntry[] = [];
  for (const item of items) {
    if (!item || typeof item !== 'object') continue;
    const o = item as Record<string, unknown>;
    const submissionId = Number(o.document_submission_id ?? o.submission_id);
    const versionId = Number(o.document_version_id ?? o.version_id);
    // Allow "error-only" entries through so the picker can show the failure
    // and let the user remove/retry. They must still be tagged by something
    // identifiable; we use either the IDs (preferred) or an explicit error.
    const hasError = typeof o.error === 'string' && o.error.length > 0;
    if (!hasError && !(isPositiveInt(submissionId) && isPositiveInt(versionId))) continue;
    const entry: FormFileEntry = {
      document_submission_id: isPositiveInt(submissionId) ? submissionId : 0,
      document_version_id: isPositiveInt(versionId) ? versionId : 0,
    };
    const filename = pickStringMaybe(o.filename);
    if (filename) entry.filename = filename;
    const mimeType = pickStringMaybe(o.mime_type) ?? pickStringMaybe(o.mimetype);
    if (mimeType) entry.mime_type = mimeType;
    if (hasError) entry.error = o.error as string;
    const pendingId = Number(o.pending_submission_id);
    if (Number.isFinite(pendingId) && pendingId > 0) {
      entry.pending_submission_id = pendingId;
    }
    entries.push(entry);
  }
  return entries;
};

/** Visible groups (filters `_lookup` etc.) — convenience wrapper. */
export const getVisibleGroups = (groups: FormGroup[]): FormGroup[] => {
  return groups.filter((g) => !isHiddenGroup(g));
};
